<p-card styleClass="mt-3">
  <!-- Table Toggle Button -->
  <p-toolbar styleClass="mb-4 gap-2">
    <ng-template pTemplate="left">
      <p-button
        label="Go to Last Due Line"
        icon="pi pi-arrow-down"
        class="p-ml-2"
        (onClick)="scrollToLastDueLine()"
        pTooltip="Scroll to the last repayment plan line that is due as of the snapshot date."
        tooltipPosition="top"
      ></p-button>
    </ng-template>

    <ng-template pTemplate="right">
      <p-button
        icon="pi pi-external-link"
        label="Download"
        (onClick)="downloadRepaymentPlanAsCSV()"
      />

      <p-button
        severity="help"
        (onClick)="copyRepaymentPlanAsCSV()"
        label="Copy As CSV"
        icon="pi pi-copy"
        class="ml-2 inline-block"
      />

      <p-button
        (click)="repaymentPlanColumnsDialog()"
        icon="pi pi-table"
        severity="contrast"
        class="ml-2 inline-block"
      />
    </ng-template>
  </p-toolbar>

  <p-dialog
    header="Repayment Plan Columns"
    [modal]="true"
    [(visible)]="showRepaymentPlanColumnsDialog"
    [style]="{ width: '50%' }"
  >
    <div class="p-fluid">
      <p-picklist
        [source]="availableRepaymentPlanCols"
        [target]="selectedRepaymentPlanCols"
        [dragdrop]="true"
        [responsive]="true"
        [sourceStyle]="{ height: '30rem' }"
        [targetStyle]="{ height: '30rem' }"
        filterBy="header"
        sourceFilterPlaceholder="Search by name"
        targetFilterPlaceholder="Search by name"
        breakpoint="1400px"
        scrollHeight="30rem"
      >
        <ng-template let-item #item>
          {{ item.header
          }}<ng-container *ngIf="developerMode !== 'Disabled'">
            ({{ item.field }})</ng-container
          >
        </ng-template>
      </p-picklist>
    </div>

    <p-divider></p-divider>

    <div class="flex justify-content-end gap-2">
      <p-button
        label="Cancel"
        severity="secondary"
        (click)="showRepaymentPlanColumnsDialog = false"
      />
      <p-button
        severity="secondary"
        label="Reset"
        (click)="resetRepaymentPlanColumns()"
      />
      <p-button label="Save" (click)="saveRepaymentPlanColumns()" />
    </div>
  </p-dialog>
  <!-- Repayment Plan Table -->

  <p-table
    #repaymentPlanTable
    [columns]="selectedRepaymentPlanCols"
    [value]="lendPeak.amortization.repaymentSchedule.entries"
    [autoLayout]="true"
    class="repayment-plan-table"
    [scrollable]="true"
    scrollHeight="600px"
  >
    <ng-template #header let-columns>
      <tr>
        <ng-container *ngFor="let col of columns">
          <th>
            {{ col.header }}
          </th>
        </ng-container>
      </tr>
    </ng-template>
    <ng-template
      #body
      let-rowData
      let-columns="columns"
      let-rowIndex="rowIndex"
    >
      <tr
        [attr.data-index]="rowIndex"
        [id]="'plan-row-' + rowIndex"
        role="row"
        [attr.aria-selected]="selectedPeriods.includes(rowData.term)"
        [class.even-row]="rowData.term % 2 === 0"
        [class.odd-row]="rowData.term % 2 !== 0"
        [class.selected-row]="selectedPeriods.includes(rowData.term)"
        (click)="onRowClick(rowData, $event)"
        tabindex="0"
        (keydown)="onRowKeyDown($event, rowData)"
      >
        <ng-container *ngFor="let col of columns">
          <td style="white-space: nowrap">
            <span [ngSwitch]="col.field">
              <!-- Number fields -->
              <ng-container *ngSwitchCase="'term'">
                {{ rowData.term | number }}
                <p-badge
                  *ngIf="!rowData.balanceModificationAmount.isZero()"
                  value="Balance Modification"
                  severity="info"
                  styleClass="mr-2"
                  pTooltip="Balance {{
                    rowData.balanceModificationAmount.greaterThan(0)
                      ? 'increased'
                      : 'decreased'
                  }} by {{
                    (rowData.balanceModificationAmount.greaterThan(0)
                      ? rowData.balanceModificationAmount.toNumber()
                      : rowData.balanceModificationAmount.abs().toNumber()
                    ) | currency
                  }}"
                  tooltipPosition="top"
                  tooltipStyleClass="wide-tooltip"
                ></p-badge>
              </ng-container>
              <ng-container *ngSwitchCase="'prebillDaysConfiguration'">
                {{ rowData.prebillDaysConfiguration | number }}
              </ng-container>
              <ng-container
                *ngSwitchCase="'billDueDaysAfterPeriodEndConfiguration'"
              >
                {{ rowData.billDueDaysAfterPeriodEndConfiguration | number }}
              </ng-container>
              <ng-container *ngSwitchCase="'calendar'">
                {{ rowData.calendar.userFriendlyName }}

                <i
                  *ngIf="rowData.metadata.isCustomCalendar"
                  severity="secondary"
                  class="pi pi-info-circle ml-2"
                  style="font-size: 1.5rem"
                  pTooltip="Custom calendar is being used for this term {{
                    rowData.calendar.userFriendlyName
                  }} instead of the default {{
                    lendPeak.amortization.calendars.primary.userFriendlyName
                  }}."
                  tooltipPosition="top"
                  tooltipStyleClass="wide-tooltip"
                ></i>
              </ng-container>

              <ng-container *ngSwitchCase="'daysInPeriod'">
                {{ rowData.daysInPeriod | number }}

                <i
                  *ngIf="rowData.metadata.isCustomCalendar"
                  severity="secondary"
                  class="pi pi-info-circle ml-2"
                  style="font-size: 1.5rem"
                  pTooltip="Custom calendar is being used for this term {{
                    rowData.calendar.userFriendlyName
                  }} instead of the default {{
                    lendPeak.amortization.calendars.primary.userFriendlyName
                  }}."
                  tooltipPosition="top"
                  tooltipStyleClass="wide-tooltip"
                ></i>
              </ng-container>

              <!-- Currency fields -->
              <ng-container *ngSwitchCase="'principal'">
                <span *ngIf="!rowData.billablePeriod">
                  <span class="pi pi-arrow-down"></span>
                </span>
                <span *ngIf="rowData.billablePeriod">
                  {{ rowData.principal.toNumber() | currency }}
                </span>
              </ng-container>
              <ng-container *ngSwitchCase="'dueInterestForTerm'">
                <span *ngIf="!rowData.billablePeriod">
                  <span class="pi pi-arrow-down"></span>
                </span>
                <span *ngIf="rowData.billablePeriod">
                  <span>
                    {{ rowData.dueInterestForTerm.toNumber() | currency }}
                  </span>
                  <span>
                    <p-badge
                      *ngIf="
                        rowData.metadata &&
                        rowData.metadata.staticInterestOverrideApplied
                      "
                      value="Manual"
                      severity="info"
                      styleClass="ml-2"
                      pTooltip="This term's interest was manually set to {{
                        rowData.metadata.actualInterestValue | currency
                      }}, calculated equivalent interest rate is {{
                        rowData.metadata.equivalentAnnualRate * 100
                          | number: '1.2-6'
                      }}%"
                      tooltipPosition="top"
                      tooltipStyleClass="wide-tooltip"
                    ></p-badge>
                  </span>
                </span>
              </ng-container>
              <ng-container *ngSwitchCase="'fees'">
                <span *ngIf="!rowData.billablePeriod">
                  <span class="pi pi-arrow-down"></span>
                </span>
                <span *ngIf="rowData.billablePeriod">
                  {{ rowData.fees.toNumber() | currency }}
                </span>
              </ng-container>
              <ng-container *ngSwitchCase="'accruedInterestForPeriod'">
                {{ rowData.accruedInterestForPeriod.toNumber() | currency }}
              </ng-container>
              <ng-container *ngSwitchCase="'billedInterestForTerm'">
                {{ rowData.billedInterestForTerm.toNumber() | currency }}
              </ng-container>
              <ng-container *ngSwitchCase="'billedDeferredInterest'">
                {{ rowData.billedDeferredInterest.toNumber() | currency }}
              </ng-container>
              <ng-container *ngSwitchCase="'unbilledTotalDeferredInterest'">
                {{
                  rowData.unbilledTotalDeferredInterest.toNumber() | currency
                }}
              </ng-container>
              <ng-container *ngSwitchCase="'billedDeferredFees'">
                <span *ngIf="!rowData.billablePeriod">
                  <span class="pi pi-arrow-down"></span>
                </span>
                <span *ngIf="rowData.billablePeriod">
                  {{ rowData.billedDeferredFees.toNumber() | currency }}
                </span>
              </ng-container>
              <ng-container *ngSwitchCase="'unbilledTotalDeferredFees'">
                <span *ngIf="!rowData.billablePeriod">
                  <span class="pi pi-arrow-down"></span>
                </span>
                <span *ngIf="rowData.billablePeriod">
                  {{ rowData.unbilledTotalDeferredFees.toNumber() | currency }}
                </span>
              </ng-container>
              <ng-container *ngSwitchCase="'endBalance'">
                {{ rowData.endBalance.toNumber() | currency }}
              </ng-container>
              <ng-container *ngSwitchCase="'startBalance'">
                {{ rowData.startBalance.toNumber() | currency }}
              </ng-container>
              <ng-container *ngSwitchCase="'balanceModificationAmount'">
                {{ rowData.balanceModificationAmount.toNumber() | currency }}
              </ng-container>
              <ng-container *ngSwitchCase="'perDiem'">
                {{ rowData.perDiem.toNumber() | currency }}
              </ng-container>
              <ng-container *ngSwitchCase="'unbilledInterestDueToRounding'">
                {{
                  rowData.unbilledInterestDueToRounding.toNumber() | currency
                }}
              </ng-container>
              
              <!-- DSI Currency fields -->
              <ng-container *ngSwitchCase="'actualDSIPrincipal'">
                <span *ngIf="rowData.actualDSIPrincipal">
                  {{ rowData.actualDSIPrincipal.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.actualDSIPrincipal">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'actualDSIInterest'">
                <span *ngIf="rowData.actualDSIInterest">
                  {{ rowData.actualDSIInterest.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.actualDSIInterest">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'actualDSIFees'">
                <span *ngIf="rowData.actualDSIFees">
                  {{ rowData.actualDSIFees.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.actualDSIFees">-</span>
              </ng-container>

              <ng-container *ngSwitchCase="'totalPayment'">
                <span *ngIf="!rowData.billablePeriod">
                  <span class="pi pi-arrow-down"></span>
                </span>
                <span *ngIf="rowData.billablePeriod">
                  {{ rowData.totalPayment.toNumber() | currency }}
                  <p-badge
                    *ngIf="
                      !rowData.metadata.isFinalEntry &&
                      lendPeak.amortization.termPaymentAmountOverride.getPaymentAmountForTerm(
                        rowData.term
                      )
                    "
                    value="PO"
                    severity="warn"
                    styleClass="ml-2"
                    pTooltip="Payment override from standard EMI {{
                      lendPeak.amortization.jsEquitedMonthlyPayment | currency
                    }} to {{
                      lendPeak.amortization.termPaymentAmountOverride.getPaymentAmountForTerm(
                        rowData.term
                      )?.jsPaymentAmount | currency
                    }}"
                    tooltipPosition="top"
                    tooltipStyleClass="wide-tooltip"
                  ></p-badge>
                </span>
              </ng-container>

              <!-- Percent fields -->
              <ng-container *ngSwitchCase="'periodInterestRate'">
                {{ rowData.periodInterestRate.toNumber() | percent: "1.2-4" }}
                <i
                  *ngIf="
                    rowData.metadata &&
                    rowData.metadata.staticInterestOverrideApplied
                  "
                  severity="secondary"
                  class="pi pi-info-circle ml-2"
                  style="font-size: 1.5rem"
                  pTooltip="This term's interest was manually set to {{
                    rowData.metadata.actualInterestValue | currency
                  }}, calculated equivalent interest rate is {{
                    rowData.metadata.equivalentAnnualRate | percent: '1.2-6'
                  }} while the loan's contractual rate is {{
                    lendPeak.amortization.jsAnnualInterestRate
                      | percent: '1.2-6'
                  }}
                    "
                  tooltipPosition="top"
                  tooltipStyleClass="wide-tooltip"
                ></i>

                <i
                  *ngIf="
                    rowData.billablePeriod &&
                    rowData.metadata &&
                    rowData.metadata.equivalentAnnualRateVarianceExceeded
                  "
                  style="color: red; font-size: 1.5rem"
                  severity="danger"
                  class="pi pi-exclamation-triangle ml-2"
                  styleClass="text-red-500"
                  pTooltip="This term's equivalent annual rate variance is {{
                    rowData.metadata.equivalentAnnualRateVariance
                      | percent: '1.2-6'
                  }} which exceeded the configured threshold of {{
                    rowData.metadata.acceptableRateVariance | percent: '1.2-6'
                  }}
                    "
                  tooltipPosition="top"
                  tooltipStyleClass="wide-tooltip"
                ></i>
              </ng-container>

              <!-- Number fields requiring decimal format -->
              <ng-container *ngSwitchCase="'interestRoundingError'">
                {{ rowData.interestRoundingError.toNumber() | number: "1.2-6" }}
              </ng-container>

              <!-- Date fields -->
              <ng-container *ngSwitchCase="'periodStartDate'">
                {{ rowData.periodStartDate.toString() |  date: "yyyy-MM-dd" }}
              </ng-container>
              <ng-container *ngSwitchCase="'periodEndDate'">
                {{ rowData.periodEndDate.toString() |  date: "yyyy-MM-dd" }}
                <p-badge
                  *ngIf="
                    rowData.billablePeriod &&
                    lendPeak.amortization.changePaymentDates.getChangePaymentDate(
                      rowData.term
                    )
                  "
                  value="CPD"
                  severity="info"
                  styleClass="mr-2"
                  pTooltip="Change Payment Date from {{
                    lendPeak.amortization.changePaymentDates.getChangePaymentDate(
                      rowData.term
                    )?.jsOriginalEndDate |   date: 'yyyy-MM-dd'
                  }} to {{
                    lendPeak.amortization.changePaymentDates.getChangePaymentDate(
                      rowData.term
                    )?.jsNewDate |   date: 'yyyy-MM-dd'
                  }}"
                  tooltipPosition="top"
                  tooltipStyleClass="wide-tooltip"
                ></p-badge>
              </ng-container>
              <ng-container *ngSwitchCase="'periodBillOpenDate'">
                <span *ngIf="!rowData.billablePeriod">
                  <span class="pi pi-arrow-down"></span>
                </span>
                <span *ngIf="rowData.billablePeriod">
                  {{
                    rowData.periodBillOpenDate.toString() |  date: "yyyy-MM-dd"
                  }}
                </span>
              </ng-container>
              <ng-container *ngSwitchCase="'periodBillDueDate'">
                <span *ngIf="!rowData.billablePeriod">
                  <span class="pi pi-arrow-down"></span>
                </span>
                <span *ngIf="rowData.billablePeriod">
                  {{
                    rowData.periodBillDueDate.toString() |  date: "yyyy-MM-dd"
                  }}
                </span>
              </ng-container>

              <!-- Boolean fields -->
              <ng-container *ngSwitchCase="'billablePeriod'">
                {{ rowData.billablePeriod === true ? "Yes" : "No" }}
              </ng-container>

              <!-- Billing Model field -->
              <ng-container *ngSwitchCase="'billingModel'">
                <span *ngIf="rowData.billingModel">
                  {{ rowData.billingModel === 'dailySimpleInterest' ? 'DSI' : 'Amortized' }}
                </span>
                <span *ngIf="!rowData.billingModel">-</span>
              </ng-container>

              <!-- DSI Number fields -->
              <ng-container *ngSwitchCase="'dsiInterestSavings'">
                <span *ngIf="rowData.dsiInterestSavings != null">
                  {{ rowData.dsiInterestSavings | currency }}
                </span>
                <span *ngIf="rowData.dsiInterestSavings == null">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'dsiInterestPenalty'">
                <span *ngIf="rowData.dsiInterestPenalty != null">
                  {{ rowData.dsiInterestPenalty | currency }}
                </span>
                <span *ngIf="rowData.dsiInterestPenalty == null">-</span>
              </ng-container>

              <!-- DSI Balance fields -->
              <ng-container *ngSwitchCase="'actualDSIStartBalance'">
                <span *ngIf="rowData.actualDSIStartBalance">
                  {{ rowData.actualDSIStartBalance.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.actualDSIStartBalance">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'actualDSIEndBalance'">
                <span *ngIf="rowData.actualDSIEndBalance">
                  {{ rowData.actualDSIEndBalance.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.actualDSIEndBalance">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'dsiInterestDays'">
                <span *ngIf="rowData.dsiInterestDays !== undefined && rowData.dsiInterestDays !== null">
                  {{ rowData.dsiInterestDays | number }}
                </span>
                <span *ngIf="rowData.dsiInterestDays === undefined || rowData.dsiInterestDays === null">-</span>
              </ng-container>

              <!-- Re-amortized fields -->
              <ng-container *ngSwitchCase="'reAmortizedStartBalance'">
                <span *ngIf="rowData.reAmortizedStartBalance">
                  {{ rowData.reAmortizedStartBalance.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.reAmortizedStartBalance">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'reAmortizedEndBalance'">
                <span *ngIf="rowData.reAmortizedEndBalance">
                  {{ rowData.reAmortizedEndBalance.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.reAmortizedEndBalance">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'reAmortizedInterest'">
                <span *ngIf="rowData.reAmortizedInterest">
                  {{ rowData.reAmortizedInterest.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.reAmortizedInterest">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'reAmortizedPrincipal'">
                <span *ngIf="rowData.reAmortizedPrincipal">
                  {{ rowData.reAmortizedPrincipal.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.reAmortizedPrincipal">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'reAmortizedFees'">
                <span *ngIf="rowData.reAmortizedFees">
                  {{ rowData.reAmortizedFees.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.reAmortizedFees">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'reAmortizedTotalPayment'">
                <span *ngIf="rowData.reAmortizedTotalPayment">
                  {{ rowData.reAmortizedTotalPayment.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.reAmortizedTotalPayment">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'reAmortizedDsiInterestDays'">
                <span *ngIf="rowData.reAmortizedDsiInterestDays !== undefined && rowData.reAmortizedDsiInterestDays !== null">
                  {{ rowData.reAmortizedDsiInterestDays | number }}
                </span>
                <span *ngIf="rowData.reAmortizedDsiInterestDays === undefined || rowData.reAmortizedDsiInterestDays === null">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'reAmortizedPerDiem'">
                <span *ngIf="rowData.reAmortizedPerDiem">
                  {{ rowData.reAmortizedPerDiem.toNumber() | currency }}
                </span>
                <span *ngIf="!rowData.reAmortizedPerDiem">-</span>
              </ng-container>

              <!-- Status flags -->
              <ng-container *ngSwitchCase="'isCurrentActiveTerm'">
                <span *ngIf="rowData.isCurrentActiveTerm === true">
                  <i class="pi pi-check" style="color: green;"></i> Yes
                </span>
                <span *ngIf="rowData.isCurrentActiveTerm !== true">-</span>
              </ng-container>
              <ng-container *ngSwitchCase="'isDelinquent'">
                <span *ngIf="rowData.isDelinquent === true">
                  <i class="pi pi-exclamation-triangle" style="color: orange;"></i> Yes
                </span>
                <span *ngIf="rowData.isDelinquent !== true">-</span>
              </ng-container>

              <!-- Metadata field -->
              <ng-container *ngSwitchCase="'metadata'">
                <table class="metadata-table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let item of rowData.metadata | keyvalue">
                      <td>{{ item.key }}</td>
                      <td>{{ item.value }}</td>
                    </tr>
                  </tbody>
                </table>
              </ng-container>

              <!-- Default case -->
              <ng-container *ngSwitchDefault>
                DEFAULT: {{ rowData[col.field] }}
              </ng-container>
            </span>
          </td>
        </ng-container>
      </tr>
    </ng-template>
  </p-table>
  <!-- Truth in Lending Disclosure Statement Button -->
  <p-divider></p-divider>
  <!-- <div class="p-mt-3 p-text-center">
    <p-button
      label="Truth in Lending Disclosure Statement"
      icon="pi pi-file"
      (onClick)="showTilaDialogButton()"
      pTooltip="View the Truth in Lending Disclosure Statement."
      tooltipPosition="top"
    ></p-button>
  </div> -->
</p-card>

<!-- Truth in Lending Disclosure Statement Dialog -->
<!-- <p-dialog
  header="Truth in Lending Disclosure Statement"
  [modal]="true"
  [(visible)]="showTilaDialog"
  [style]="{ width: '90%' }"
>
  <app-tila-disclosure
    [showTitle]="false"
    [lenderName]="lenderName"
    [borrowerName]="borrowerName"
    [loanDate]="loanDate"
    [loanNumber]="loanNumber"
    [collateralDescription]="collateralDescription"
    [prepaymentPenalty]="prepaymentPenalty"
    [latePaymentGracePeriod]="latePaymentGracePeriod"
    [latePaymentFee]="latePaymentFee"
    [assumable]="assumable"
  ></app-tila-disclosure>
</p-dialog> -->
